import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Card, CardMedia, Typography, Dialog, DialogContent, Box, Grid, Button, Snackbar, Alert, } from '@mui/material';
import { useParams } from 'react-router-dom';
import RewardRevealPopup from '../components/Reward.js';
import SubscriptionDialog from '../components/SubscriptionDialog';
import { useNotifications } from '../contexts/NotificationsContext';
import {
  acknowledgeRewardQuotaNotices,
  createImageSignupInvite,
  getFriendGalleryByUsername,
  getRewardGallery,
  getRewardStatus,
  getSignupInviteStatus,
  getUnseenRewardImages,
  processRewardJob,
  setProfileImage,
} from '../utils/api';
import { LoadingIndicator } from '../components/assets';
import { formatLocalDateTime } from '../utils/dateTime';

const GALLERY_BATCH_SIZE = 12;
const MAX_SIMULATED_IMAGE_LOAD_DELAY_MS = 10000;

const getSimulatedImageLoadDelay = () => {
  const requestedDelay = Number(new URLSearchParams(window.location.search).get('simulateImageLoad'));
  if (!Number.isFinite(requestedDelay) || requestedDelay <= 0) return 0;
  return Math.min(requestedDelay, MAX_SIMULATED_IMAGE_LOAD_DELAY_MS);
};

const formatRewardCountdown = (expiresAt, nowMs) => {
  const remainingSeconds = Math.max(
    0,
    Math.ceil((new Date(expiresAt).getTime() - nowMs) / 1000),
  );
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const Gallery = () => {
  const { userName } = useParams();
  const isFriendGallery = !!userName;
  const { setUnseenRewards } = useNotifications();
  const [images, setImages] = useState([]);
  const [galleryOwner, setGalleryOwner] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [currentUnseen, setCurrentUnseen] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [profileImageId, setProfileImageId] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [signupInvitesEnabled, setSignupInvitesEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [galleryBatchLoading, setGalleryBatchLoading] = useState(false);
  const [bufferedImageCount, setBufferedImageCount] = useState(0);
  const [hasMoreImages, setHasMoreImages] = useState(false);
  const [readyUnseenIds, setReadyUnseenIds] = useState(() => new Set());
  const [pendingRewardJobs, setPendingRewardJobs] = useState([]);
  const [blockedRewards, setBlockedRewards] = useState([]);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [countdownNow, setCountdownNow] = useState(() => Date.now());
  const [galleryRefreshNonce, setGalleryRefreshNonce] = useState(0);
  const galleryBufferRef = useRef([]);
  const nextGalleryCursorRef = useRef(null);
  const imagePreloadPromisesRef = useRef(new Map());
  const galleryBatchLoadingRef = useRef(false);
  const galleryRequestGenerationRef = useRef(0);
  const loadMoreTriggerRef = useRef(null);
  const simulatedImageLoadDelayRef = useRef(getSimulatedImageLoadDelay());
  const pendingRewardJobKey = pendingRewardJobs
    .map((job) => `${job.id}:${job.status}:${job.attempt_count}`)
    .join('|');
  const activeBlockedRewards = blockedRewards.filter(
    (job) => new Date(job.expires_at).getTime() > countdownNow,
  );
  const nextBlockedReward = activeBlockedRewards[0] || null;
  const rewardCountdown = nextBlockedReward
    ? formatRewardCountdown(nextBlockedReward.expires_at, countdownNow)
    : null;

  const preloadImage = useCallback((imageUrl) => {
    if (!imageUrl) return Promise.resolve(false);

    const existing = imagePreloadPromisesRef.current.get(imageUrl);
    if (existing) return existing;

    const promise = new Promise((resolve) => {
      const image = new Image();
      image.onload = async () => {
        try {
          await image.decode?.();
        } catch (_) {
          // The browser can still paint an image when decoding is unavailable or interrupted.
        }
        if (simulatedImageLoadDelayRef.current) {
          await new Promise((resolve) => setTimeout(resolve, simulatedImageLoadDelayRef.current));
        }
        resolve(true);
      };
      image.onerror = () => resolve(false);
      image.decoding = 'async';
      image.src = imageUrl;
    });

    imagePreloadPromisesRef.current.set(imageUrl, promise);
    return promise;
  }, []);

  const fetchGalleryPage = useCallback((cursor = null) => {
    const options = { limit: GALLERY_BATCH_SIZE, cursor };
    return isFriendGallery
      ? getFriendGalleryByUsername(userName, options)
      : getRewardGallery(options);
  }, [isFriendGallery, userName]);

  const prefetchGalleryPage = useCallback(async (cursor, requestGeneration) => {
    if (!cursor || galleryBatchLoadingRef.current) return;
    galleryBatchLoadingRef.current = true;
    setGalleryBatchLoading(true);
    try {
      const data = await fetchGalleryPage(cursor);
      if (galleryRequestGenerationRef.current !== requestGeneration) return;
      const nextImages = data.images || [];
      await Promise.all(nextImages.map((image) => preloadImage(image.thumbnail_url)));
      if (galleryRequestGenerationRef.current !== requestGeneration) return;
      galleryBufferRef.current = nextImages;
      nextGalleryCursorRef.current = data.next_cursor || null;
      setBufferedImageCount(nextImages.length);
      setHasMoreImages(nextImages.length > 0);
      if (data.friend) setGalleryOwner(data.friend);
    } catch (err) {
      if (galleryRequestGenerationRef.current === requestGeneration) {
        console.error('Failed to prefetch gallery page:', err);
        setLoadError(err.data?.message || err.message || 'Failed to load more rewards.');
        setHasMoreImages(false);
      }
    } finally {
      if (galleryRequestGenerationRef.current === requestGeneration) {
        galleryBatchLoadingRef.current = false;
        setGalleryBatchLoading(false);
      }
    }
  }, [fetchGalleryPage, preloadImage]);

  const loadNextGalleryBatch = useCallback(() => {
    if (galleryBatchLoadingRef.current || galleryBufferRef.current.length === 0) return;

    const nextImages = galleryBufferRef.current;
    galleryBufferRef.current = [];
    setBufferedImageCount(0);
    setImages((currentImages) => {
      const existingIds = new Set(currentImages.map((image) => image.id));
      return [...currentImages, ...nextImages.filter((image) => !existingIds.has(image.id))];
    });

    const cursor = nextGalleryCursorRef.current;
    if (cursor) {
      prefetchGalleryPage(cursor, galleryRequestGenerationRef.current);
    } else {
      setHasMoreImages(false);
    }
  }, [prefetchGalleryPage]);

  useEffect(() => {
    let alive = true;
    const requestGeneration = galleryRequestGenerationRef.current + 1;
    galleryRequestGenerationRef.current = requestGeneration;
    const isRefresh = galleryRefreshNonce > 0;
    if (!isRefresh) {
      setLoaded(false);
    }
    setLoadError(null);
    if (!isRefresh) {
      setSelectedImage(null);
      setPopupOpen(false);
      setCurrentUnseen([]);
      setReadyUnseenIds(new Set());
      setImages([]);
      setPendingRewardJobs([]);
      imagePreloadPromisesRef.current.clear();
    }
    galleryBufferRef.current = [];
    nextGalleryCursorRef.current = null;
    galleryBatchLoadingRef.current = false;
    setBufferedImageCount(0);
    setGalleryBatchLoading(false);
    setHasMoreImages(false);

    const loadGallery = async () => {
      try {
        const [data, statusData, inviteStatus] = await Promise.all([
          fetchGalleryPage(),
          isFriendGallery ? Promise.resolve(null) : getRewardStatus(),
          isFriendGallery
            ? Promise.resolve(null)
            : getSignupInviteStatus().catch(() => ({ enabled: false })),
        ]);
        const firstImages = data.images || [];
        await Promise.all(firstImages.map((image) => preloadImage(image.thumbnail_url)));
        if (!alive || galleryRequestGenerationRef.current !== requestGeneration) return;

        setGalleryOwner(isFriendGallery ? data.friend || { username: userName } : null);
        setImages(firstImages);
        nextGalleryCursorRef.current = data.next_cursor || null;
        setHasMoreImages(Boolean(data.next_cursor));
        setLoaded(true);

        if (data.next_cursor) {
          prefetchGalleryPage(data.next_cursor, requestGeneration);
        }

        if (isFriendGallery) return;

        setSignupInvitesEnabled(inviteStatus?.enabled === true);

        const activeRewardJobs = (statusData.reward_jobs || []).filter((job) => (
          job.status === 'pending' || job.status === 'processing'
        ));
        setPendingRewardJobs(activeRewardJobs);
        const currentBlockedRewards = statusData.blocked_rewards || [];
        setBlockedRewards(currentBlockedRewards);
        if (currentBlockedRewards.some((job) => !job.quota_notice_seen_at)) {
          setSubscriptionDialogOpen(true);
        }

        if ((statusData.unseen_count || 0) > 0) {
          const unseenData = await getUnseenRewardImages();
          if (!alive || galleryRequestGenerationRef.current !== requestGeneration) return;
          const unseen = unseenData.images || [];
          setCurrentUnseen(unseen);
          setPopupOpen(true);
          unseen.forEach((image) => {
            preloadImage(image.thumbnail_url).then((didLoad) => {
              if (alive && didLoad) {
                setReadyUnseenIds((currentIds) => new Set(currentIds).add(image.id));
              }
            });
          });
        } else if (activeRewardJobs.length > 0) {
          setCurrentUnseen([]);
          setPopupOpen(true);
          setReadyUnseenIds(new Set());
        } else {
          setPopupOpen(false);
          setCurrentUnseen([]);
        }
      } catch (err) {
        console.error('Failed to fetch gallery:', err);
        if (!alive) return;
        setLoadError(err.data?.message || err.message || 'Failed to load gallery.');
        setLoaded(true);
      }
    };

    loadGallery();

    return () => {
      alive = false;
    };
  }, [fetchGalleryPage, galleryRefreshNonce, isFriendGallery, prefetchGalleryPage, preloadImage, userName]);

  useEffect(() => {
    const trigger = loadMoreTriggerRef.current;
    if (!trigger || !loaded || !hasMoreImages || galleryBatchLoading) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadNextGalleryBatch();
      },
      { rootMargin: '200px' }
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [bufferedImageCount, galleryBatchLoading, hasMoreImages, images.length, loadNextGalleryBatch, loaded]);

  useEffect(() => {
    if (isFriendGallery || pendingRewardJobs.length === 0) return undefined;

    let cancelled = false;
    processRewardJob()
      .catch((error) => {
        console.error('Failed to process pending reward:', error);
      })
      .finally(() => {
        if (!cancelled) {
          window.dispatchEvent(new CustomEvent('rewardsUpdate'));
          setGalleryRefreshNonce((value) => value + 1);
        }
      });

    const pollId = setInterval(() => {
      setGalleryRefreshNonce((value) => value + 1);
    }, 5000);

    return () => {
      cancelled = true;
      clearInterval(pollId);
    };
  }, [isFriendGallery, pendingRewardJobKey, pendingRewardJobs.length]);

  useEffect(() => {
    if (isFriendGallery || blockedRewards.length === 0) return undefined;
    const intervalId = setInterval(() => {
      const now = Date.now();
      setCountdownNow(now);
      if (blockedRewards.some(
        (job) => new Date(job.expires_at).getTime() <= now,
      )) {
        const remaining = blockedRewards.filter(
          (job) => new Date(job.expires_at).getTime() > now,
        );
        setBlockedRewards(remaining);
        if (remaining.length === 0) setSubscriptionDialogOpen(false);
        window.dispatchEvent(new CustomEvent('rewardsUpdate'));
        setGalleryRefreshNonce((value) => value + 1);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [blockedRewards, isFriendGallery]);

  useEffect(() => {
    if (isFriendGallery) return;

    fetch('/api/users/me', {
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => setProfileImageId(data.profile_image_id || null))
      .catch(err => console.error('Failed to fetch profile image state:', err));
  }, [isFriendGallery]);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    console.log('Selected image:', image);
  };

  const handleClose = () => {
    setSelectedImage(null);
  };

  const handleRewardClose = async () => {
    if (currentUnseen.length === 0) {
      setPopupOpen(false);
      return;
    }
    const imageId = currentUnseen[0].id;
    // Mark as seen
    await fetch(`/api/images/${imageId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ been_seen: true }),
      credentials: 'include',
    }).catch(err => console.error('Failed to mark image as seen:', err));
    // Update local images
    setImages(prev => prev.map(img => img.id === imageId ? { ...img, been_seen: true } : img));
    galleryBufferRef.current = galleryBufferRef.current.map(
      img => img.id === imageId ? { ...img, been_seen: true } : img
    );
    // Decrement unseen count
    setUnseenRewards(prev => Math.max(0, prev - 1));
    window.dispatchEvent(new CustomEvent('rewardsUpdate'));

    const remaining = currentUnseen.slice(1);
    try {
      const data = await getUnseenRewardImages();
      const existingIds = new Set(remaining.map((image) => image.id));
      const replenished = [
        ...remaining,
        ...(data.images || []).filter((image) => !existingIds.has(image.id)),
      ].slice(0, 2);
      await Promise.all(replenished.map((image) => preloadImage(image.thumbnail_url)));
      replenished.forEach((image) => {
        setReadyUnseenIds((currentIds) => new Set(currentIds).add(image.id));
      });
      setCurrentUnseen(replenished);
      setPopupOpen(replenished.length > 0);
    } catch (err) {
      console.error('Failed to load the next unseen reward:', err);
      setCurrentUnseen(remaining);
      setPopupOpen(remaining.length > 0);
    }
  };

  const handleDownload = async () => {
    if (!selectedImage) return;
    try {
      const res = await fetch(`/api/images/${selectedImage.id}/download`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data.download_url) {
        const a = document.createElement('a');
        a.href = data.download_url;
        a.download = `${selectedImage.image_class || 'reward'}_${selectedImage.id}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        setSnackbar({ open: true, message: 'Failed to get download link.', severity: 'error' });
      }
    } catch (error) {
      console.error('Download failed:', error);
      setSnackbar({ open: true, message: 'Download failed.', severity: 'error' });
    }
  };

  const handleShare = async () => {
    if (!selectedImage) return;
    try {
      const res = await fetch(`/api/images/${selectedImage.id}/share`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.share_url) {
        await navigator.clipboard.writeText(data.share_url);
        setSnackbar({ open: true, message: 'Share link copied to clipboard!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: 'Failed to create share link.', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to create share link.', severity: 'error' });
    }
  };

  const handleShareSignupInvite = async () => {
    if (!selectedImage) return;
    try {
      const data = await createImageSignupInvite(selectedImage.id);
      if (!data.invite_url) throw new Error('Missing invitation link.');
      await navigator.clipboard.writeText(data.invite_url);
      setSnackbar({ open: true, message: 'Picture and invite link copied!', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.data?.message || 'Failed to create picture invitation.',
        severity: 'error',
      });
    }
  };

  const handleSetProfileImage = async () => {
    if (!selectedImage) return;
    setProfileSaving(true);
    try {
      const data = await setProfileImage(selectedImage.id);
      setProfileImageId(data.profile_image_id || selectedImage.id);
      window.dispatchEvent(new CustomEvent('profileUpdate'));
      setSnackbar({ open: true, message: 'Profile pic updated!', severity: 'success' });
    } catch (error) {
      console.error('Failed to update profile pic:', error);
      setSnackbar({ open: true, message: error.data?.message || 'Failed to update profile pic.', severity: 'error' });
    } finally {
      setProfileSaving(false);
    }
  };

  const acknowledgeCurrentBlockedRewards = async () => {
    const unacknowledgedIds = activeBlockedRewards
      .filter((job) => !job.quota_notice_seen_at)
      .map((job) => job.id);
    if (unacknowledgedIds.length === 0) return;
    try {
      await acknowledgeRewardQuotaNotices(unacknowledgedIds);
      const acknowledgedAt = new Date().toISOString();
      setBlockedRewards((current) => current.map((job) => (
        unacknowledgedIds.includes(job.id)
          ? { ...job, quota_notice_seen_at: acknowledgedAt }
          : job
      )));
    } catch (error) {
      console.error('Failed to acknowledge reward limit notice:', error);
    }
  };

  const handleSubscriptionClose = () => {
    setSubscriptionDialogOpen(false);
    acknowledgeCurrentBlockedRewards();
  };

  const handleSubscriptionAccessGranted = () => {
    setSubscriptionDialogOpen(false);
    acknowledgeCurrentBlockedRewards();
    window.dispatchEvent(new CustomEvent('billingStatusUpdate'));
    window.dispatchEvent(new CustomEvent('rewardsUpdate'));
    setGalleryRefreshNonce((value) => value + 1);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!loaded) {
    return (
      <Container id="gallery-loading" sx={{ minHeight: '50vh', display: 'grid', placeItems: 'center' }}>
        <LoadingIndicator text="Getting rewards" />
      </Container>
    );
  }

  return (
    <Container id="gallery-page">
      <Typography variant="h4" component="h1" gutterBottom>
        {isFriendGallery ? `${galleryOwner?.username || userName}'s Reward Gallery` : 'Reward Gallery'}
      </Typography>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {!isFriendGallery && nextBlockedReward && (
        <Alert
          id="reward-quota-banner"
          severity="warning"
          sx={{ mb: 2, border: '2px solid', borderColor: 'warning.main' }}
          action={(
            <Button
              color="inherit"
              onClick={() => setSubscriptionDialogOpen(true)}
            >
              Subscribe
            </Button>
          )}
        >
          {activeBlockedRewards.length === 1
            ? 'Your reward is waiting.'
            : `${activeBlockedRewards.length} rewards are waiting.`}
          {' '}Payment must be confirmed within {rewardCountdown}, or
          {activeBlockedRewards.length === 1 ? ' this reward' : ' these rewards'} will expire.
        </Alert>
      )}
<Grid
  id="gallery-grid"
  container
  spacing={2}
  justifyContent="center"
>
  {images.map((img) => (
    <Grid
      item
      key={img.id}
      size={{ xs: 6, md: 4, lg: 3 }}
    >
      <Card
        id={`gallery-image-${img.id}`}
        sx={{
          border: '2px solid',
          borderColor: 'secondary.main',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Square wrapper */}
          <CardMedia
            component="img"
            image={img.thumbnail_url}
            alt={`Generated image ${img.id}`}
            onClick={() => handleImageClick(img)}
            decoding="async"
          />
      </Card>
    </Grid>
  ))}
</Grid>
      {hasMoreImages && (
        <Box
          ref={loadMoreTriggerRef}
          sx={{ minHeight: 150, display: 'grid', placeItems: 'center', py: 3 }}
        >
          {galleryBatchLoading && <LoadingIndicator text="Getting more rewards" />}
        </Box>
      )}
      {selectedImage && (
        <Dialog id="gallery-image-dialog" open={!!selectedImage} onClose={handleClose}
  maxWidth={false}  sx={{
    '& .MuiDialog-container': {
      padding: 0,   // removes the 32px padding from the CONTAINER
    },
  }}

    PaperProps={{
    sx: {
      width: 'fit-content',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: '100dvh',
      margin: 0, // removes margin on the PAPER
      borderRadius: 0,
    },
  }}

  >
          <DialogContent sx={{ p: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}>
             <Box
              id="gallery-selected-image"
              component="img"
              src={selectedImage.thumbnail_url}
              alt={`Generated image ${selectedImage.id}`}
              sx={{
                width: 'auto',
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '75dvh',
                objectFit: 'contain',
                borderRadius: '8px',
                border: '2px solid',
                borderColor: 'secondary.main', // ← theme color works!
              }}
            />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Reward Recieved on: {formatLocalDateTime(selectedImage.generated_at)}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 2 }}>
              {!isFriendGallery && (
                <>
                  <Button
                    id="gallery-set-profile-image"
                    variant="contained"
                    color="primary"
                    onClick={handleSetProfileImage}
                    disabled={profileSaving || selectedImage.id === profileImageId}
                  >
                    {selectedImage.id === profileImageId ? 'Current Profile Pic' : 'Set as Profile Pic'}
                  </Button>
                  <Button id="gallery-download" variant="contained" color="primary" onClick={handleDownload}>
                    Download
                  </Button>
                  <Button id="gallery-share" variant="contained" color="secondary" onClick={handleShare}>
                    Share
                  </Button>
                  {signupInvitesEnabled && (
                    <Button
                      id="gallery-share-signup-invite"
                      variant="contained"
                      color="primary"
                      onClick={handleShareSignupInvite}
                    >
                      Send Picture and Invite
                    </Button>
                  )}
                </>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      )}
      {!isFriendGallery && (
        <RewardRevealPopup
          key={currentUnseen[0]?.id || 'pending-reward'}
          open={popupOpen}
          imageUrl={currentUnseen[0]?.thumbnail_url || ''}
          title={currentUnseen[0]?.image_class || ''}
          imageReady={readyUnseenIds.has(currentUnseen[0]?.id)}
          loadingTitle={currentUnseen.length === 0 ? 'Reward is being generated' : undefined}
          loadingText={currentUnseen.length === 0 ? 'Your reward is still being created.' : undefined}
          allowCloseWhileLoading={currentUnseen.length === 0}
          onClose={handleRewardClose}
        />
      )}
      {!isFriendGallery && (
        <SubscriptionDialog
          open={subscriptionDialogOpen && Boolean(nextBlockedReward)}
          onClose={handleSubscriptionClose}
          onAccessGranted={handleSubscriptionAccessGranted}
          title="Your reward is waiting"
          closeLabel="Back to rewards"
          message={(
            <>
              Payment must be confirmed within <strong>{rewardCountdown}</strong> to
              generate {activeBlockedRewards.length === 1 ? 'this reward' : 'these rewards'}.
              {' '}Expired rewards cannot be recovered.
            </>
          )}
        />
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Gallery;
